import { useState } from 'react';

const PHONE = '[PHONE NUMBER]';
const EMAIL = '[EMAIL ADDRESS]';
const FORM_URL = 'https://readdy.ai/api/form/d76ltdtr5g02gnd6a3bg';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setFormState('submitting');
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name) formData.append(el.name, el.value);
    }

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-surface">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block w-8 h-0.5 bg-accent"></span>
            <span className="text-accent font-semibold text-sm tracking-widest uppercase">Get In Touch</span>
            <span className="block w-8 h-0.5 bg-accent"></span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy leading-tight mb-4">
            Ready to Schedule Your Inspection?
          </h2>
          <p className="text-gray-500 text-base leading-relaxed">
            Reach out today to get started. We&apos;ll make the process easy and keep you informed every step of the way.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-white rounded-lg p-6 border border-gray-100">
              <h3 className="font-heading font-bold text-navy text-lg mb-5">Contact Information</h3>
              <div className="flex flex-col gap-5">
                <a
                  href={`tel:${PHONE}`}
                  className="flex items-start gap-4 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-md flex-shrink-0 group-hover:bg-accent transition-colors">
                    <i className="ri-phone-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-0.5">Phone</p>
                    <p className="text-navy font-semibold text-sm group-hover:text-accent transition-colors">{PHONE}</p>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-start gap-4 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-md flex-shrink-0 group-hover:bg-accent transition-colors">
                    <i className="ri-mail-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-0.5">Email</p>
                    <p className="text-navy font-semibold text-sm group-hover:text-accent transition-colors">{EMAIL}</p>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-md flex-shrink-0">
                    <i className="ri-map-pin-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-0.5">Location</p>
                    <p className="text-navy font-semibold text-sm">Gaylord, Michigan</p>
                    <p className="text-gray-400 text-xs">Serving Northern Michigan</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3">
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.querySelector('#contact form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-accent hover:bg-accent-light text-white font-semibold rounded-md cursor-pointer transition-colors whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line"></i>
                </div>
                Schedule an Inspection
              </a>
              <a
                href={`tel:${PHONE}`}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-navy hover:bg-navy-light text-white font-semibold rounded-md cursor-pointer transition-colors whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                Call Now
              </a>
            </div>

            {/* Trust note */}
            <div className="flex items-start gap-3 p-4 bg-accent-muted rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-accent text-base"></i>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                We respond quickly and are committed to making the scheduling process easy for you.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3 bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <h3 className="font-heading font-bold text-navy text-lg mb-6">Send Us a Message</h3>

            {formState === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-4">
                  <i className="ri-check-double-line text-green-500 text-3xl"></i>
                </div>
                <h4 className="font-heading font-bold text-navy text-xl mb-2">Message Sent!</h4>
                <p className="text-gray-500 text-sm max-w-xs">
                  Thank you for reaching out. We&apos;ll be in touch with you shortly to confirm your inspection.
                </p>
                <button
                  onClick={() => setFormState('idle')}
                  className="mt-6 px-6 py-2.5 bg-accent text-white text-sm font-semibold rounded-md cursor-pointer hover:bg-accent-light transition-colors whitespace-nowrap"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-navy text-sm font-medium">Full Name <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder="Jane Smith"
                      className="px-4 py-2.5 border border-gray-200 rounded-md text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-navy text-sm font-medium">Email Address <span className="text-red-400">*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="px-4 py-2.5 border border-gray-200 rounded-md text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="phone" className="text-navy text-sm font-medium">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="(231) 555-0100"
                    className="px-4 py-2.5 border border-gray-200 rounded-md text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="property_address" className="text-navy text-sm font-medium">Property Address</label>
                  <input
                    type="text"
                    id="property_address"
                    name="property_address"
                    placeholder="123 Main St, Gaylord, MI 49735"
                    className="px-4 py-2.5 border border-gray-200 rounded-md text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-navy text-sm font-medium">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your inspection needs, preferred dates, or any questions you have..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="px-4 py-2.5 border border-gray-200 rounded-md text-sm text-navy placeholder-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition resize-none"
                  />
                  <p className={`text-xs text-right ${charCount > 450 ? 'text-amber-500' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                {formState === 'error' && (
                  <p className="text-red-500 text-sm bg-red-50 px-4 py-2.5 rounded-md">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="mt-2 w-full px-6 py-3.5 bg-accent hover:bg-accent-light disabled:opacity-60 text-white font-semibold rounded-md cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {formState === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-send-plane-line"></i>
                      </div>
                      Schedule an Inspection
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
